import { SectionHeader, NavButtons } from "@/components/workflow/ui";
import { Button } from "@/components/ui/button";
import FollowupQuestion from "@/components/workflow/FollowupQuestion";
import { HelpCircle, ClipboardCopy, NotebookPen } from "lucide-react";
import { toast } from "sonner";

function buildSnapshot(questions, answers) {
  const qs = questions.questions || [];
  const pos = [], neg = [];
  qs.forEach((q) => {
    const a = (answers[q.id] || "").trim();
    if (!a) return;
    (((q.type || "").toLowerCase() === "negative") ? neg : pos).push(`• ${q.question} → ${a}`);
  });
  return [
    "PERTINENT POSITIVES:",
    pos.length ? pos.join("\n") : "• (none recorded)",
    "",
    "PERTINENT NEGATIVES (rule-outs):",
    neg.length ? neg.join("\n") : "• (none recorded)",
  ].join("\n");
}

function FollowupActions({ onCopy, onToNotes }) {
  return (
    <div className="mb-6 flex flex-wrap gap-2">
      <Button type="button" variant="outline" size="sm" onClick={onCopy} data-testid="copy-snapshot-btn" className="border-zinc-300 text-zinc-700">
        <ClipboardCopy className="mr-2 h-4 w-4" /> Copy +/− summary
      </Button>
      <Button type="button" variant="outline" size="sm" onClick={onToNotes} data-testid="snapshot-to-notes-btn" className="border-zinc-300 text-zinc-700">
        <NotebookPen className="mr-2 h-4 w-4" /> Add to case notes
      </Button>
    </div>
  );
}

export default function StepFollowup({ questions, answers, setAnswers, reminded, addSnapshotToNotes, onBack, onNext }) {
  const copySnapshot = async () => {
    try {
      await navigator.clipboard.writeText(buildSnapshot(questions, answers));
      toast.success("Positives / negatives summary copied to clipboard");
    } catch {
      toast.error("Could not copy. Your browser blocked clipboard access.");
    }
  };
  const toNotes = () => addSnapshotToNotes(buildSnapshot(questions, answers));
  const setAnswer = (id, val) => setAnswers((a) => ({ ...a, [id]: val }));

  return (
    <div data-testid="followup-section">
      <SectionHeader icon={HelpCircle} sub="Step 2 · Patient" title="A few follow-up questions" />
      {questions.intro && <p className="text-sm text-zinc-600 -mt-2 mb-4">{questions.intro}</p>}
      <FollowupActions onCopy={copySnapshot} onToNotes={toNotes} />
      <div className="space-y-5">
        {questions.questions?.map((q, i) => (
          <FollowupQuestion
            key={q.id || i}
            q={q}
            index={i}
            value={answers[q.id] || ""}
            blank={reminded && !(answers[q.id] || "").trim()}
            onChange={(val) => setAnswer(q.id, val)}
          />
        ))}
      </div>
      <NavButtons onBack={onBack} onNext={onNext} nextLabel="Generate tentative diagnosis" nextTestId="submit-answers-btn" />
    </div>
  );
}
